import React, { useState, useRef, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { CamProp, SNAP_PX } from '../components/timeline/timelineConstants';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CamDragState {
  prop: CamProp | 'summary';
  fromFrame: number;
  toFrame: number;
}

interface Options {
  trackScrollRef: React.RefObject<HTMLDivElement | null>;
  pxPerFrame: number;
  totalFrames: number;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useCameraKfDrag({ trackScrollRef, pxPerFrame, totalFrames }: Options) {
  const setCameraKeyframe    = useAppStore((s) => s.setCameraKeyframe);
  const removeCameraKeyframe = useAppStore((s) => s.removeCameraKeyframe);
  const setCurrentFrame      = useAppStore((s) => s.setCurrentFrame);

  const [camDragState, setCamDragState] = useState<CamDragState | null>(null);
  const camDragRef      = useRef<CamDragState | null>(null);
  const camDragMovedRef = useRef(false);

  const handleCamKfPointerDown = useCallback(
    (e: React.PointerEvent, prop: CamProp | 'summary', frame: number) => {
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const drag = { prop, fromFrame: frame, toFrame: frame };
      camDragRef.current = drag;
      camDragMovedRef.current = false;
      setCamDragState(drag);
    },
    []
  );

  const handleCamKfPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!camDragRef.current) return;
      const cursorPx = e.clientX - (trackScrollRef.current?.getBoundingClientRect().left ?? 0) + (trackScrollRef.current?.scrollLeft ?? 0);
      let newFrame = Math.max(0, Math.min(totalFrames - 1, Math.round(cursorPx / pxPerFrame)));
      if (e.shiftKey) {
        const cf = Math.round(useAppStore.getState().currentFrame);
        for (const f of [0, totalFrames - 1, cf]) {
          if (Math.abs(cursorPx - f * pxPerFrame) <= SNAP_PX) { newFrame = f; break; }
        }
      }
      if (newFrame !== camDragRef.current.toFrame) {
        const next = { ...camDragRef.current, toFrame: newFrame };
        camDragRef.current = next;
        camDragMovedRef.current = true;
        setCamDragState({ ...next });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pxPerFrame, totalFrames]
  );

  const handleCamKfPointerUp = useCallback(
    (e: React.PointerEvent, prop: CamProp | 'summary', fromFrame: number) => {
      const drag = camDragRef.current;
      if (!drag || drag.prop !== prop || drag.fromFrame !== fromFrame) return;
      const { toFrame } = drag;
      if (camDragMovedRef.current && fromFrame !== toFrame) {
        const camKfs = useAppStore.getState().cameraKeyframes;
        if (prop === 'summary') {
          if (fromFrame in camKfs) {
            const val = camKfs[fromFrame];
            viewportActions.pushSnapshot.current();
            removeCameraKeyframe(fromFrame);
            setCameraKeyframe(toFrame, val);
          }
        } else {
          if (fromFrame in camKfs) {
            const val = camKfs[fromFrame];
            const updated = { ...val, [prop]: val[prop] };
            viewportActions.pushSnapshot.current();
            removeCameraKeyframe(fromFrame);
            setCameraKeyframe(toFrame, updated);
          }
        }
        setCurrentFrame(toFrame);
      }
      camDragRef.current = null;
      setCamDragState(null);
    },
    [removeCameraKeyframe, setCameraKeyframe, setCurrentFrame]
  );

  return { camDragState, camDragMovedRef, handleCamKfPointerDown, handleCamKfPointerMove, handleCamKfPointerUp };
}
